import React, { createContext, useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { UserLogin } from 'src/services';
import { setUserInfo } from 'src/store/user/action';
import { useDispatch } from 'react-redux';
import { setJobInfo } from 'src/store/job/action';
import apiClient from 'src/services/Api/api';
import { sanitizeSessionData, sanitizeJsonString, readSessionUser } from 'src/helpers/sanitizeSessionUser';
const initialState = {
  user: null,
  isInitialised: false,
  isAuthenticated: false,
  userType: 'admin',
  permissions: [],
  permissionsStatus: 'loading', // 'loading' | 'loaded' | 'error'
};

// const isValidToken = (accessToken) => {
//   if (!accessToken) return false;

//   const decodedToken = jwtDecode(accessToken);
//   const currentTime = Date.now() / 1000;
//   return decodedToken.exp > currentTime;
// };


const setSession = (accessToken, accessUser) => {
  return new Promise((resolve) => {
    if (accessToken) {
      localStorage.setItem('@accessToken', accessToken);
      localStorage.setItem('@accessUser', sanitizeJsonString(accessUser));
      axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
    } else {
      localStorage.removeItem('@accessToken');
      localStorage.removeItem('@accessUser');
      delete axios.defaults.headers.common.Authorization;
    }
    resolve();
  });
};



// R3A-1 : charge les droits du compte courant depuis le serveur (source de verite).
// Ne stocke RIEN dans localStorage. Distingue loaded (200) de error (401/403/5xx/reseau).
// Renvoie { permissions, permissionsStatus }.
const chargerPermissions = async () => {
  try {
    const res = await apiClient.get('/api/permissions/me');
    if (!Array.isArray(res?.data?.permissions)) {
      return { permissions: [], permissionsStatus: 'error' };
    }
    return { permissions: res.data.permissions, permissionsStatus: 'loaded' };
  } catch (e) {
    // 401/403/500/reseau -> error (JAMAIS confondu avec zero droit)
    return { permissions: [], permissionsStatus: 'error' };
  }
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'INIT': {
      const { isAuthenticated, user } = action.payload;
      const userType = user?.role === 'employee' || user?.isEmployee ? 'employee' : 'admin';
      return { ...state, isAuthenticated, isInitialised: true, user, userType };
    }
    case 'SET_PERMISSIONS': {
      const { permissions, permissionsStatus } = action.payload;
      return { ...state, permissions, permissionsStatus };
    }
    case 'LOGIN': {
      const { user } = action.payload;
      const userType = user?.role === 'employee' || user?.isEmployee ? 'employee' : 'admin';
      return { ...state, isAuthenticated: true, user, userType };
    }
    case 'LOGOUT': {
      return { ...state, isAuthenticated: false, user: null, userType: 'admin', permissions: [], permissionsStatus: 'loading' };
    }
    case 'REGISTER': {
      const { user } = action.payload;
      const userType = user?.role === 'employee' || user?.isEmployee ? 'employee' : 'admin';
      return { ...state, isAuthenticated: true, user, userType };
    }

    default:
      return state;
  }
};

const AuthContext = createContext({
  ...initialState,
  method: 'JWT',
  login: () => { },
  logout: () => { },
  register: () => { },
  checklogin: () => { },
  loginEmploye: () => { }

});

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const dispatchM = useDispatch();
  const login = async (email, password) => {
    try {
      const response = await UserLogin.UserLogin({ email, password });
      if (response) {
        const fullUserData = {
          ...response.user,
          ...response.uservalidate,
          name: response.user?.firstname || response.additionalData?.firstname,
          lastname: response.user?.lastname || response.additionalData?.lastname,
          image: response.user?.image || response.additionalData?.image,
        };

        await Promise.all([
          setSession(response.token, JSON.stringify(fullUserData)),
          dispatch({ type: 'LOGIN', payload: { user: sanitizeSessionData(fullUserData) } })
        ]);
        // R3A-1 : vider les droits precedents + loading AVANT chargement.
        dispatch({ type: 'SET_PERMISSIONS', payload: { permissions: [], permissionsStatus: 'loading' } });
        dispatch({ type: 'SET_PERMISSIONS', payload: await chargerPermissions() });
        document.body.classList.remove('employee-mode');
        return true;
      } else {
        setSession();
        return false;
      }
    } catch (error) {
      console.error('Login failed:', error);
      setSession();
      return false;
    }

  };
  const loginEmploye = async (email, password) => {
    try {
      const response = await UserLogin.UserLoginEmploye({ email, password });
      if (response) {
        const fullUserData = {
          ...response.user,
          ...response.uservalidate,
          name: response.uservalidate?.firstname || response.user?.firstname,
          lastname: response.uservalidate?.lastname || response.user?.lastname,
          image: response.uservalidate?.image || response.user?.image
        };
        setSession(response.token, JSON.stringify(fullUserData))
        // Dispatch the LOGIN action only if the user data is valid
        if (fullUserData) {
          dispatch({ type: 'LOGIN', payload: { user: sanitizeSessionData(fullUserData) } });
          // R3A-1 : un employe n'a pas de permissions admin -> loaded + [], sans appel /me.
          dispatch({ type: 'SET_PERMISSIONS', payload: { permissions: [], permissionsStatus: 'loaded' } });
        }
        // Dispatch the setUserInfo action only if userValidate exists
        if (response.uservalidate) {
          dispatchM(setUserInfo(response.uservalidate));
        }

        if (response.uservalidate) {

          dispatchM(setJobInfo({
            jobentityadminstration:
              response.uservalidate.jobentityadminstration,

            jobname:
              response.uservalidate.jobname
          }));

        }
        document.body.classList.add('employee-mode');
        return true;
      } else {
        setSession()
        // return false
      }


    } catch (error) {
      console.error('Login failed:', error);
      setSession()
      return false;
    }
  };
  const register = async (email, username, password) => {
    const response = await axios.post('/api/auth/register', { email, username, password });
    const { user } = response.data;

    dispatch({ type: 'REGISTER', payload: { user } });
  };

  const logout = () => {
    setSession()
    document.body.classList.remove('employee-mode');
    dispatch({ type: 'LOGOUT' });
  };
  const checklogin = () => {
    const accessToken = localStorage.getItem('@accessToken');
    if (accessToken) {
      try {
        const userData = readSessionUser();
        if (userData) {
          dispatch({ type: 'INIT', payload: { isAuthenticated: true, user: userData } });
          return true
        } else {
          console.error('User data not found in localStorage');
          dispatch({ type: 'INIT', payload: { isAuthenticated: false, user: null } });
          return false
        }
      } catch (error) {
        console.error('Error parsing user data from localStorage:', error);
        dispatch({ type: 'INIT', payload: { isAuthenticated: false, user: null } });
        return false
      }
    } else {
      dispatch({ type: 'INIT', payload: { isAuthenticated: false, user: null } });
      return false
    }
  }
  useEffect(() => {
    (async () => {
      const accessToken = localStorage.getItem('@accessToken');
      if (accessToken) {
        try {
          const userData = readSessionUser();
          if (userData) {
            dispatch({ type: 'INIT', payload: { isAuthenticated: true, user: userData } });
            if (userData.role === 'employee' || userData.isEmployee) {
              dispatch({ type: 'SET_PERMISSIONS', payload: { permissions: [], permissionsStatus: 'loaded' } });
            } else {
              dispatch({ type: 'SET_PERMISSIONS', payload: { permissions: [], permissionsStatus: 'loading' } });
              dispatch({ type: 'SET_PERMISSIONS', payload: await chargerPermissions() });
            }
            // Apply mode according to the user type
            if (userData.role === 'employee' || userData.isEmployee) {
              document.body.classList.add('employee-mode');
            } else {
              document.body.classList.remove('employee-mode');
            }
          } else {
            console.error('User data not found in localStorage');
            dispatch({ type: 'INIT', payload: { isAuthenticated: false, user: null } });
          }
        } catch (error) {
          console.error('Error parsing user data from localStorage:', error);
          dispatch({ type: 'INIT', payload: { isAuthenticated: false, user: null } });
        }
      } else {
        dispatch({ type: 'INIT', payload: { isAuthenticated: false, user: null } });
      }
    })();
  }, []);
  // SHOW LOADER
  if (!state.isInitialised) return <p>hee</p>;

  return (
    <AuthContext.Provider value={{ ...state, method: 'JWT', login, logout, register, checklogin, loginEmploye }}>
      {children}
    </AuthContext.Provider>
  );
};
AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default AuthContext;
